<script setup lang="ts">
import { IUseForm } from '@/BaseInterfaces'
import { VPasswordInput, VTextInput } from '@/Components/BaseComponents/Form'
import { PageProps } from '@/Models/Inertia'
import AuthenticationLayout from '@/Pages/Auth/AuthenticationLayout.vue'
import FormButtons from '@/Pages/Auth/FormButtons.vue'
import { Head, router, useForm, usePage } from '@inertiajs/vue3'
import { route } from 'ziggy-js'

const page = usePage<PageProps>()
const appDetails = page.props.app_details

if (!appDetails.allow_registrations) {
    router.visit(route('login'))
}

// Get invite code from URL params - supports ?invite=, ?accountant=, or ?distributor=
const urlParams = new URLSearchParams(window.location.search)
const inviteCode =
    urlParams.get('invite') || urlParams.get('accountant') || urlParams.get('distributor') || ''
const inviteCodeFromUrl = !!inviteCode // Track if code came from URL

const form = useForm<IUseForm>({
    email: '',
    password: '',
    password_confirmation: '',
    terms: true,
    invite_code: inviteCode,
    // Honeypot: onzichtbaar voor mensen, wordt door scripts die alle velden invullen
    // wél gevuld. De backend weigert dan (RegistrationFormGuard, GH #6481).
    extra_field: '',
})

const submit = () => {
    // Het formulier-token komt uit de page props en niet uit de initiële form-state:
    // na een validatiefout rendert Inertia dezelfde pagina met een vers token.
    form.transform((data) => ({ ...data, form_token: page.props.form_token })).post(
        route('register'),
        {
            onFinish: () => form.reset('password', 'password_confirmation'),
        }
    )
}
</script>

<template>
    <Head title="Register" />

    <authentication-layout v-if="appDetails.allow_registrations">
        <template #header>
            <h1 class="text-center mb-8 text-">
                <strong>{{ $t('auth.register_today') }}</strong> <br />
                {{ $t('auth.and_try') }}
            </h1>
        </template>

        <template #form>
            <div class="edit-create">
                <form @submit.prevent="submit">
                    <VTextInput
                        :label="$t('auth.email')"
                        type="email"
                        :name="'email'"
                        v-model="form.email"
                        required
                        id="email"
                        autofocus
                        autocomplete="username"
                        :placeholder="$t('auth.email')"
                    />

                    <v-password-input
                        :label="$t('auth.password')"
                        :name="'password'"
                        v-model="form.password"
                        id="password"
                        required
                        autocomplete="current-password"
                        :placeholder="$t('auth.password')"
                    />

                    <v-password-input
                        :label="$t('auth.password_confirmation')"
                        :name="'password_confirmation'"
                        v-model="form.password_confirmation"
                        id="password"
                        required
                        autocomplete="current-password"
                        :placeholder="$t('auth.password_confirmation')"
                    />
                    <VTextInput
                        v-if="inviteCode"
                        :label="$t('auth.invite_code')"
                        :name="'invite_code'"
                        v-model="form.invite_code"
                        id="invite_code"
                        :placeholder="$t('auth.invite_code_placeholder')"
                        :disabled="inviteCodeFromUrl"
                        :readonly="inviteCodeFromUrl"
                    />

                    <div
                        class="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden"
                        aria-hidden="true"
                    >
                        <label for="extra_field">Extra</label>
                        <input
                            id="extra_field"
                            name="extra_field"
                            type="text"
                            v-model="form.extra_field"
                            tabindex="-1"
                            autocomplete="off"
                        />
                    </div>

                    <form-buttons
                        :cancel-button-link="route('login')"
                        :cancel-button-text="$t('auth.to_login')"
                        :submit-button-text="$t('auth.register')"
                    />

                    <div
                        v-if="appDetails && appDetails.terms_of_use_link"
                        class="mt-4 text-center md:text-start lg:grid lg:grid-cols-3 gap-x-4"
                    >
                        <div class="hidden lg:block" />
                        <div class="col-start-2 col-span-3 mt-4 text-center">
                            <span class="text-sm">
                                {{ $t('auth.automatic_accept') }}
                                <a
                                    :href="appDetails.terms_of_use_link"
                                    target="_blank"
                                    class="text-sm font-bold"
                                >
                                    {{ $t('auth.terms_of_use') }}
                                </a>
                            </span>
                        </div>
                    </div>
                </form>
            </div>
        </template>
    </authentication-layout>
</template>
<style scoped lang="scss">
h1 {
    @apply text-2xl;
    strong {
        @apply font-bold text-2xl;
    }
}
</style>
